import Big from 'big.js';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import * as lineitemsapi from 'smg/customer-api/lineitems';
import * as session from 'smg/customer-api/session';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { Draw } from '../address';
import * as payments from '../payments';
import { PromptLoginCode } from './login';

declare interface myWindow extends Window {
    googleReviews: string;
}
declare const window: myWindow;
declare interface orderResp {
    order: models.Order;
}

export default async function main() {
    const sess = await session.Session().catch(() => undefined);
    const customerName = document.querySelector('#customer-name') as HTMLElement;
    const newPassword = document.querySelector('#create-password-form') as HTMLElement;
    const spinner = document.querySelector('#spinner');

    if (!sess) {
        customerName.remove();
        const notLoggedInText = document.querySelector('#logged-out-text') as HTMLParagraphElement;
        const loggedInText = document.querySelector('#logged-in-text') as HTMLParagraphElement;

        loggedInText.classList.add('d-none');
        notLoggedInText.classList.remove('d-none');

        newPassword.classList.remove('d-none');
    } else {
        customerName.textContent = sess?.firstName;
    }

    const notFoundEl = document.querySelector('#order-not-found') as HTMLElement;
    const orderFoundEl = document.querySelector('#order-found') as HTMLElement;
    const tableBody = document.querySelector('#table-body') as HTMLElement;
    const orderDate = document.querySelector('#order-date') as HTMLElement;
    const orderNumber = document.querySelector('#order-number') as HTMLElement;
    const shippingContainer = document.querySelector('#shipping-options-container') as HTMLElement;
    const userCreated = document.querySelector('#user-created') as HTMLElement;

    const key = G().Get('id');

    if (!key) {
        orderFoundEl.classList.add('d-none');
        notFoundEl.classList.remove('d-none');
        (spinner as HTMLElement).classList.add('d-none');
        return;
    }

    axios.get<orderResp>(`/orders/${key}`).
        then(async resp => {
            const order = resp.data.order;

            if (window.googleReviews) {
                const m = new Modal({
                    title: 'We\'d love to hear your feedback!',
                    text: 'If we exceeded your expectations, please share it with a review. (This will open a new tab)',
                    confirmButtonClass: 'btn btn-primary text-white rounded',
                    cancelButtonClass: 'btn btn-link text-muted',
                    confirmText: 'Leave a Review!',
                    cancelText: 'No Thank You',
                    onSubmit: () => window.open(window.googleReviews, '_blank'),
                });

                const modalFooterArr = Array.from(m.Element.querySelectorAll('.modal-footer'));
                const modalFooter = modalFooterArr[0];
                modalFooter.classList.add('d-flex', 'flex-column', 'flex-sm-row');
                const googleReviewIcon = template.Clone('#google-review-logo-template') as HTMLElement;
                googleReviewIcon.firstElementChild?.classList.add('me-sm-auto');

                modalFooter?.insertBefore(googleReviewIcon, modalFooter.firstElementChild);
            }

            newPassword.addEventListener('submit', async e => {
                e.preventDefault();

                const password = document.querySelector('#password') as HTMLInputElement;
                const email = document.querySelector('#email-prefill') as HTMLInputElement;
                email.value = order.orderShippingOptions?.[0].shippingOption?.email ?? '';

                await axios.post('/users', {
                    'fullName': order.orderShippingOptions?.[0].shippingOption?.fullName,
                    'company': order.orderShippingOptions?.[0].shippingOption?.company,
                    'email': order.orderShippingOptions?.[0].shippingOption?.email,
                    'phone': order.orderShippingOptions?.[0].shippingOption?.phone,
                    'password': password.value,
                    'confirmPassword': password.value,
                }).then(async () => {
                    const codeEntered = await PromptLoginCode({ email: order.orderShippingOptions?.[0].shippingOption?.email });

                    void codeEntered.login.then(() => {
                        newPassword.classList.add('d-none');
                        userCreated.classList.remove('d-none');
                    });
                });
            });

            orderNumber.textContent = (order.invoiceNumber ?? 0).toString();
            orderDate.textContent = DateTime.fromISO(order.created ?? '').toLocaleString(DateTime.DATETIME_SHORT);
            const proofEls: Element[] = [];

            const price = document.querySelector('#price') as HTMLElement;
            const priceInfoBtn = document.querySelector('#price-info-btn') as HTMLElement;
            const morePriceInfoContainer = document.querySelector('#more-price-info') as HTMLElement;
            const lineItemContainer = document.querySelector('#line-item-container') as HTMLElement;

            price.textContent = DollarFormat(order.total ?? '');

            priceInfoBtn.addEventListener('click', () => {
                if (morePriceInfoContainer.classList.contains('d-none')) {
                    morePriceInfoContainer.classList.remove('d-none');
                    priceInfoBtn.textContent = 'Less Info';
                } else {
                    morePriceInfoContainer.classList.add('d-none');
                    priceInfoBtn.textContent = 'More Info';
                }
            });

            const prevBtn = document.querySelector('#previous') as HTMLElement;
            const nextBtn = document.querySelector('#next') as HTMLElement;

            if (order.orderProofs?.length === 1) {
                prevBtn.classList.add('d-none');
                nextBtn.classList.add('d-none');
            }

            for (const orderProof of order.orderProofs || []) {
                const rowEl = document.querySelector('#row-template') as HTMLElement;
                const proofEl = template.Clone('#thumbnail-template').firstElementChild as HTMLElement;
                proofEls.push(proofEl);
                const imgEl = proofEl.querySelector('img') as HTMLImageElement;
                const anchorEl = proofEl.querySelector('a') as HTMLAnchorElement;
                const thumbnailContainer = rowEl.querySelector('#thumbnail-container') as HTMLElement;

                anchorEl.href = orderProof?.proof?.main ?? '';
                anchorEl.dataset.caption = orderProof?.proof?.name;

                (imgEl as HTMLImageElement).src = orderProof?.proof?.thumbnail ?? '';

                thumbnailContainer.appendChild(proofEl);

                tableBody.append(rowEl);
            }

            let total = Big('0');
            const orderSubtotal = document.querySelector('#subtotal') as HTMLElement;
            const shippingCost = document.querySelector('#shipping-cost') as HTMLElement;
            const taxCost = document.querySelector('#tax-cost') as HTMLElement;
            const discounts = document.querySelector('#discounts') as HTMLElement;

            let shippingCostNum = Big('0');
            let taxCostNum = Big('0');
            let discountNum = Big('0');

            if (order?.orderLineItems?.length) {
                for (const lineItem of order.orderLineItems) {
                    if (Big(lineItem.price || 0).lt(0)) {
                        discountNum = discountNum.add(lineItem.price || 0);
                    }

                    const subtotal = Big(lineItem.price || 0).mul(lineItem.quantity || 0);
                    total = total.add(subtotal);

                    if (!lineItem.lineItemModel?.shipping && !lineItem.lineItemModel?.tax) {
                        const lineItemEl = template.Clone('#line-item-template') as HTMLElement;
                        const description = lineItemEl.querySelector('#item-description') as HTMLElement;
                        const quantity = lineItemEl.querySelector('#quantity') as HTMLElement;
                        const listContainer = lineItemEl.querySelector('.child-list-items') as HTMLElement;
                        const moreInfoBtn = lineItemEl.querySelector('#more-info-btn') as HTMLElement;
                        const collapseEl = lineItemEl.querySelector('.accordion-collapse') as HTMLElement;
                        const collapseTarget = lineItemEl.querySelector('#collapse-target') as HTMLElement;
                        collapseEl.id = `child-line-items-${UUID()}`;
                        moreInfoBtn.setAttribute('data-bs-target', `#${collapseEl.id}`);
                        collapseTarget.addEventListener('show.bs.collapse', () => {
                            moreInfoBtn.textContent = 'Less Info';
                        });
                        collapseTarget.addEventListener('hide.bs.collapse', () => {
                            moreInfoBtn.textContent = 'More Info';
                        });

                        const childLineItems = (lineItem as models.OrderLineItem).orderChildLineItems || [];
                        for (const childLineItem of childLineItems) {
                            const subtotal = Big(childLineItem.price || 0).mul(childLineItem.quantity || 0);
                            total = total.add(subtotal);

                            const listEl = document.createElement('li');
                            const option = childLineItem.childLineItemOption?.name || childLineItem.option;
                            listEl.textContent = `${(childLineItem.childLineItem?.name ?? '') || (childLineItem.child ?? '')} ${option ? ` - ${option}` : ''} + ${DollarFormat(subtotal)}`;
                            listContainer.append(listEl);

                            if (Big(childLineItem.price || 0).lt(0)) {
                                discountNum = discountNum.add(childLineItem.price || 0);
                            }
                        }

                        description.textContent = [
                            (await lineitemsapi.LineItemMap()).get(lineItem.lineItemID ?? '')?.pluralName || lineItem.lineItem,
                            (await lineitemsapi.TypeMap()).get(lineItem.lineItemTypeID ?? '')?.name || lineItem.type,
                            (await lineitemsapi.SizeMap()).get(lineItem.lineItemTypeSizeID ?? '')?.name || lineItem.size,
                            lineItem.description,
                        ].filter(Boolean).join(' - ');

                        quantity.textContent = (lineItem.quantity ?? 0).toString();
                        lineItemContainer.append(lineItemEl);
                    } else if (lineItem.lineItemModel?.shipping) {
                        shippingCostNum = shippingCostNum.add(Big(lineItem.price ?? '') || 0);
                    } else {
                        taxCostNum = taxCostNum.add(Big(lineItem.price ?? '') || 0);
                    }
                }
            }

            const lineItemEls = Array.from(lineItemContainer.children);

            if (lineItemEls.length === 1) {
                lineItemEls?.[0].firstElementChild?.classList.remove('border-bottom');
            } else {
                lineItemEls[lineItemEls.length - 1].classList.remove('border-bottom');
            }

            orderSubtotal.textContent = DollarFormat(total.sub(taxCostNum).sub(shippingCostNum).sub(discountNum));
            discounts.textContent = DollarFormat(discountNum);
            shippingCost.textContent = DollarFormat(shippingCostNum);
            taxCost.textContent = DollarFormat(taxCostNum);

            let currProof = 0;

            const showHideProofs = () => {
                for (let i = 0; i < proofEls.length; i++) {
                    if (i === currProof) {
                        proofEls[i].classList.remove('d-none');
                    } else {
                        proofEls[i].classList.add('d-none');
                    }
                }
            };
            nextBtn.addEventListener('click', () => {
                currProof++;
                if (currProof >= proofEls.length) {
                    currProof = 0;
                }
                showHideProofs();
            });
            prevBtn.addEventListener('click', () => {
                currProof--;
                if (currProof < 0) {
                    currProof = proofEls.length - 1;
                }
                showHideProofs();
            });

            showHideProofs();

            for (const shippingAddress of order.orderShippingOptions as models.OrderShippingOption[]) {
                const addressEl = await Draw(shippingAddress.shippingOption as models.ShippingOption, shippingAddress.shippingOption?.phone, shippingAddress.shippingOption?.email, template.Clone('#shipping-options-template'));
                shippingContainer.append(addressEl);
            }

            const cartPaymentContainer = document.querySelector('#cart-payment-container') as HTMLElement;
            const poCartPaymentContainer = document.querySelector('#po-cart-payment-container') as HTMLElement;
            const nonPaymentWarning = document.querySelector('#non-payment-warning') as HTMLElement;
            const poNonPaymentWarning = document.querySelector('#po-non-payment-warning') as HTMLElement;
            const cartPaymentWarnIcon = document.querySelector('#warning-icon') as HTMLElement;
            const poCartPaymentWarnIcon = document.querySelector('#po-warning-icon') as HTMLElement;
            const totalNum = document.querySelector('#balance-total') as HTMLElement;
            const poTotalNum = document.querySelector('#po-balance-total') as HTMLElement;
            const paidNum = document.querySelector('#balance-payment') as HTMLElement;
            const poPaidNum = document.querySelector('#po-balance-payment') as HTMLElement;
            const balanceNum = document.querySelector('#balance-remaining') as HTMLElement;
            const poBalanceNum = document.querySelector('#po-balance-remaining') as HTMLElement;
            const makePaymentBtn = document.querySelectorAll('.make-payment-btn');
            const paidInFullText = document.querySelector('#paid-in-full-text') as HTMLElement;
            const balanceRemaining = document.querySelector('#balance-remaining-text') as HTMLElement;

            const balance = Big(order.balance || 0);
            if (balance.gt('0.05')) {
                makePaymentBtn.forEach(btn => btn.addEventListener('click', () =>
                    payments.PromptPayment(sess, [order.id as string], balance, false).
                        then(() => window.location.reload()),
                ));
            }

            function hasPo(order: models.Order) {
                if (order.orderPayments) {
                    for (const orderPayment of order.orderPayments) {
                        if ((orderPayment.paymentOption as models.PaymentOption).paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                            return true;
                        }
                    }
                    return false;
                }
            }

            if (!hasPo(order) && balance.gt('0.05')) {
                paidInFullText.classList.add('d-none');
                balanceRemaining.classList.remove('d-none');
                cartPaymentContainer.classList.remove('d-none');
                totalNum.innerText = DollarFormat(order.total || 0);
                paidNum.innerText = DollarFormat(order.paid || 0);
                balanceNum.innerText = DollarFormat(order.balance || 0);
                cartPaymentContainer.classList.add('alert', 'alert-warning', 'w-75');
                cartPaymentWarnIcon.classList.remove('d-none');
                nonPaymentWarning.classList.remove('d-none');
                poCartPaymentContainer.classList.add('d-none');
            } else if (hasPo(order) && balance.gt('0.05')) {
                poCartPaymentContainer.classList.remove('d-none');
                poCartPaymentContainer.classList.add('d-flex');
                poTotalNum.innerText = DollarFormat(order.total || 0);
                poPaidNum.innerText = DollarFormat(order.paid || 0);
                poBalanceNum.innerText = DollarFormat(order.balance || 0);
                poCartPaymentContainer.classList.add('alert', 'alert-success', 'w-75');
                poCartPaymentWarnIcon.classList.remove('d-none');
                poNonPaymentWarning.classList.remove('d-none');
                cartPaymentContainer.classList.remove('alert', 'alert-warning');
                cartPaymentWarnIcon.classList.add('d-none');
                nonPaymentWarning.classList.add('d-none');
                cartPaymentContainer.classList.add('d-none');
            } else {
                poCartPaymentContainer.classList.remove('alert', 'alert-warning');
                poCartPaymentWarnIcon.classList.add('d-none');
                poNonPaymentWarning.classList.add('d-none');
                poCartPaymentContainer.classList.add('d-none');
                cartPaymentContainer.classList.remove('alert', 'alert-warning');
                cartPaymentWarnIcon.classList.add('d-none');
                nonPaymentWarning.classList.add('d-none');
                cartPaymentContainer.classList.add('d-none');
            }

            const orderContainer = document.querySelector('#order-found');
            (spinner as Element).classList.add('d-none');
            orderContainer?.classList.remove('d-none');
        }).
        catch(err => {
            console.error(err);
            (spinner as Element).classList.add('d-none');
            orderFoundEl.classList.add('d-none');
            notFoundEl.classList.remove('d-none');
        });
}